import { useEffect, useMemo, useState } from 'react';
import {
  FullEngagementFragment,
  GetSubmissionsDocument,
  GetSubmissionsQuery,
  RaffleAdminConfig,
  RaffleAdminData,
  RaffleStatus,
  RaffleSubmissionData,
  RaffleViewConfig,
  RaffleViewData,
  useDeleteSubmissionMutation,
  useGetSubmissionsQuery,
  useOnMockSubmissionCreatedSubscription,
  useOnMockSubmissionDeletedSubscription,
  useOnSubmissionCreatedSubscription,
  useOnSubmissionDeletedSubscription,
  useRaffleBeginDrawingMutation,
  useRaffleCloseMutation,
  useRaffleReopenMutation,
  useRaffleResetMutation,
  useRaffleRestartDrawingMutation,
} from '@/generated/graphql';
import {
  TypedEngagement,
  asTypedEngagement,
  asTypedSubmissionQueryResult,
} from '@/utils/engagementTypeUtils';
import { Button } from '@/components/ui/button';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { getRaffleWinnerIds } from '../winners';
import { RaffleTicket } from '../RaffleTicket';
import { raffleTicketSerial } from '../drawing/ticketMotion';
import { RaffleStatusChip } from '../drawing/RaffleStatusChip';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MinusIcon, PlusIcon } from '@radix-ui/react-icons';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import {
  RAFFLE_ADMIN_STATUS_LABEL,
  canRemoveAdminRaffleTicket,
  clampRaffleWinnerCount,
  isAdminRaffleTicketDimmed,
  isAdminRaffleTicketWinner,
} from './adminControl';
import { ClaimYourPrizeInput } from './ClaimYourPrizeInput';
import { displaySubmitterName } from '@/utils/displaySubmitterName';

const PRIMARY_BUTTON_CLASS =
  'bg-[#FF6B3D] text-black hover:bg-[#FF6B3D]/90 border-0';
const SECONDARY_BUTTON_CLASS =
  'border-2 border-white bg-transparent text-white hover:bg-white/10';

function TicketIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={className}
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path d="M3 8.5C3 7.12 4.12 6 5.5 6h13C19.88 6 21 7.12 21 8.5v1.2c-1 .5-1.5 1.2-1.5 2.3s.5 1.8 1.5 2.3v1.2c0 1.38-1.12 2.5-2.5 2.5h-13C4.12 18 3 16.88 3 15.5v-1.2c1-.5 1.5-1.2 1.5-2.3S4 9.2 3 8.7V8.5Z" />
      <path d="M12 6v12" strokeDasharray="2 2" />
    </svg>
  );
}

function RaffleAdminControlViewInner({
  engagement,
}: {
  engagement: TypedEngagement<
    RaffleAdminData,
    RaffleAdminConfig,
    RaffleViewData,
    RaffleViewConfig
  >;
}) {
  const engagementId = engagement.id!;
  const { data: submissionsData } = useGetSubmissionsQuery({
    variables: { engagementId },
    skip: !engagement.id,
  });
  const typedSubmissions = asTypedSubmissionQueryResult<RaffleSubmissionData>(
    submissionsData || { submissions: [] },
  );
  const submissions = useMemo(
    () => typedSubmissions.submissions ?? [],
    [typedSubmissions.submissions],
  );
  const entryCount = submissions.length;

  const [winnerCount, setWinnerCount] = useState(() =>
    clampRaffleWinnerCount(engagement.data.winnerCount ?? 1, entryCount),
  );
  const [resetOpen, setResetOpen] = useState(false);
  const [fewEntriesOpen, setFewEntriesOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);

  const mockRealtime = useMockRealtimeChannel();
  useRaffleAdminSubmissionSync(engagementId, mockRealtime);

  const [closeRaffle, { loading: closing }] = useRaffleCloseMutation();
  const [reopenRaffle, { loading: reopening }] = useRaffleReopenMutation();
  const [beginDrawing, { loading: beginning }] =
    useRaffleBeginDrawingMutation();
  const [resetRaffle, { loading: resetting }] = useRaffleResetMutation();
  const [restartDrawing, { loading: restarting }] =
    useRaffleRestartDrawingMutation();
  const [deleteSubmission, { loading: deleting }] =
    useDeleteSubmissionMutation();

  const status = engagement.data.raffleStatus;
  const mutating =
    closing || reopening || beginning || resetting || restarting || deleting;
  const canRemove = canRemoveAdminRaffleTicket(status);
  const remainingIds = engagement.data.remainingSubmissionIds ?? [];
  const winnerIds = getRaffleWinnerIds(engagement.data);
  const fewerEntriesThanWinners = entryCount < winnerCount;
  const submissionsQuery = {
    query: GetSubmissionsDocument,
    variables: { engagementId },
  };

  useEffect(() => {
    setWinnerCount((current) => clampRaffleWinnerCount(current, entryCount));
  }, [entryCount]);

  const runBeginDrawing = () =>
    beginDrawing({
      variables: {
        engagementId,
        winnerCount,
      },
    });

  const handleBeginDrawingClick = () => {
    if (winnerCount < 1 || entryCount === 0) return;
    if (fewerEntriesThanWinners) {
      setFewEntriesOpen(true);
      return;
    }
    void runBeginDrawing();
  };

  const handleRemoveTicket = (submissionId: string) => {
    if (!canRemove) return;
    void deleteSubmission({
      variables: { submissionId },
      refetchQueries: [submissionsQuery],
      update: (cache) => {
        cache.evict({ id: `Submission:${submissionId}` });
      },
    });
  };

  return (
    <div
      data-name="RAFFLE_ADMIN_CONTROL_VIEW"
      className="flex h-full w-full flex-col bg-black text-white"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
        <h2 className="mb-1 flex flex-row flex-wrap items-center gap-2 text-2xl font-bold">
          <TicketIcon className="h-7 w-7 text-white" />
          <span>{engagement.title}</span>
          <RaffleStatusChip tone="orange" data-name="RAFFLE_STATUS">
            {RAFFLE_ADMIN_STATUS_LABEL[status] ?? status}
          </RaffleStatusChip>
          <RaffleStatusChip tone="muted" data-name="RAFFLE_ENTRY_COUNT">
            {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
          </RaffleStatusChip>
        </h2>

        {submissions.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-sm text-white/45">No tickets yet.</p>
          </div>
        ) : (
          <ul
            data-name="RAFFLE_ADMIN_TICKET_GRID"
            className="grid grid-cols-4 gap-2 md:grid-cols-6 md:gap-3"
          >
            {submissions.map((submission) => {
              const dimmed = isAdminRaffleTicketDimmed({
                status,
                submissionId: submission.id,
                remainingIds,
                winnerIds,
              });
              const isWinner = isAdminRaffleTicketWinner({
                status,
                submissionId: submission.id,
                winnerIds,
              });
              const serial = raffleTicketSerial(submission.id);
              const submitter = displaySubmitterName(
                submission.user,
                submission.userId,
              );
              const ticket = (
                <RaffleTicket
                  visibleSide="back"
                  signatureImageUrl={submission.data?.optionalImageUrl}
                  dimmed={dimmed}
                  isWinner={isWinner}
                  compactWinner={isWinner}
                  showWinnerBadge={isWinner}
                  winnerBadgePlacement="on-ticket"
                  className="w-full max-w-[160px]"
                />
              );

              return (
                <li
                  key={submission.id}
                  data-name="RAFFLE_ADMIN_TICKET_CELL"
                  className="relative flex flex-col items-center"
                >
                  {canRemove ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-name="RAFFLE_ADMIN_TICKET"
                          className="w-full max-w-[160px] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B3D]"
                          aria-label={`Ticket ${serial}`}
                        >
                          {ticket}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        data-name="RAFFLE_ADMIN_TICKET_MENU"
                        align="end"
                        className="border-white/20 bg-zinc-950 text-white"
                      >
                        <DropdownMenuItem
                          className="text-red-400 focus:bg-red-950 focus:text-red-300"
                          onSelect={() => handleRemoveTicket(submission.id)}
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <div
                      data-name="RAFFLE_ADMIN_TICKET"
                      className="w-full max-w-[160px]"
                    >
                      {ticket}
                    </div>
                  )}
                  <span className="pointer-events-none absolute bottom-1 left-2 font-mono text-[9px] text-black/70">
                    {serial.slice(-4)}
                  </span>
                  <span
                    data-name="RAFFLE_ADMIN_SUBMITTER"
                    className="mt-1 w-full max-w-[160px] truncate text-center text-[10px] text-white/55"
                    title={submitter.title ?? submitter.text}
                  >
                    {submitter.text}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-3 border-t border-white/10 p-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="raffle-claim-message"
            className="text-[11px] text-white/40"
          >
            Claim your prize
          </label>
          <ClaimYourPrizeInput
            id="raffle-claim-message"
            className="border-white/30 bg-transparent text-white placeholder:text-white/35 focus-visible:ring-[#FF6B3D]"
          />
        </div>

        {(status === RaffleStatus.Open ||
          status === RaffleStatus.ReadyToDraw) && (
          <p className="text-[11px] text-white/40">
            Tap a ticket to remove · Remove only in Open / Ready to draw
          </p>
        )}

        {status === RaffleStatus.ReadyToDraw && fewerEntriesThanWinners && (
          <p
            data-name="RAFFLE_FEWER_ENTRIES_WARNING"
            className="rounded-full border border-[rgba(255,107,61,0.45)] bg-[rgba(255,107,61,0.12)] px-3 py-1 text-xs text-[#FF6B3D]"
          >
            Fewer entries than requested winners — confirm before drawing.
          </p>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          {status === RaffleStatus.ReadyToDraw && (
            <WinnerStepper
              value={winnerCount}
              entryCount={entryCount}
              disabled={mutating}
              onChange={setWinnerCount}
            />
          )}

          <div
            className={cn(
              'grid w-full grid-cols-1 gap-2 sm:grid-cols-2 md:ml-auto md:flex md:w-auto md:flex-row',
              status !== RaffleStatus.ReadyToDraw && 'md:justify-end',
            )}
          >
            {status === RaffleStatus.Open && (
              <>
                <Button
                  data-name="RAFFLE_CLOSE"
                  className={cn(PRIMARY_BUTTON_CLASS, 'w-full md:w-auto')}
                  disabled={mutating}
                  onClick={() => closeRaffle({ variables: { engagementId } })}
                >
                  Close entries
                </Button>
                <Button
                  data-name="RAFFLE_RESET"
                  variant="outline"
                  className={cn(SECONDARY_BUTTON_CLASS, 'w-full md:w-auto')}
                  disabled={mutating}
                  onClick={() => setResetOpen(true)}
                >
                  Reset
                </Button>
              </>
            )}

            {status === RaffleStatus.ReadyToDraw && (
              <>
                <Button
                  data-name="RAFFLE_BEGIN_DRAWING"
                  className={cn(PRIMARY_BUTTON_CLASS, 'w-full md:w-auto')}
                  disabled={mutating || winnerCount < 1 || entryCount === 0}
                  onClick={handleBeginDrawingClick}
                >
                  Begin drawing
                </Button>
                <Button
                  data-name="RAFFLE_REOPEN"
                  variant="outline"
                  className={cn(SECONDARY_BUTTON_CLASS, 'w-full md:w-auto')}
                  disabled={mutating}
                  onClick={() => reopenRaffle({ variables: { engagementId } })}
                >
                  Re-open entries
                </Button>
              </>
            )}

            {status === RaffleStatus.Drawing && (
              <Button
                data-name="RAFFLE_RESET"
                variant="outline"
                className={cn(
                  SECONDARY_BUTTON_CLASS,
                  'w-full sm:col-start-2 md:w-auto',
                )}
                disabled={mutating}
                onClick={() => setResetOpen(true)}
              >
                Reset
              </Button>
            )}

            {status === RaffleStatus.Completed && (
              <Button
                data-name="RAFFLE_RESTART_DRAWING"
                className={cn(PRIMARY_BUTTON_CLASS, 'w-full md:w-auto')}
                disabled={mutating}
                onClick={() => setRestartOpen(true)}
              >
                Restart drawing
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmActionDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={
          status === RaffleStatus.Drawing
            ? 'Halt this drawing?'
            : 'Reset this raffle?'
        }
        description={
          status === RaffleStatus.Drawing
            ? 'Halt draw and restore removed tickets to Ready to draw?'
            : 'Clear all tickets and return to Open?'
        }
        confirmLabel="Reset"
        confirmVariant="destructive"
        loading={resetting}
        contentClassName="border-white/15 bg-zinc-950 text-white"
        onConfirm={() =>
          resetRaffle({
            variables: { engagementId },
            refetchQueries: [submissionsQuery],
          })
        }
      />

      <ConfirmActionDialog
        open={restartOpen}
        onOpenChange={setRestartOpen}
        title="Restart drawing?"
        description="Return to Ready to draw and keep tickets. Winners will be cleared."
        confirmLabel="Restart drawing"
        confirmVariant="epic"
        loading={restarting}
        contentClassName="border-white/15 bg-zinc-950 text-white"
        onConfirm={() => restartDrawing({ variables: { engagementId } })}
      />

      <ConfirmActionDialog
        open={fewEntriesOpen}
        onOpenChange={setFewEntriesOpen}
        title="Fewer entries than winners"
        description={`There are ${entryCount} ${
          entryCount === 1 ? 'entry' : 'entries'
        } but you asked for ${winnerCount} winners. Begin drawing anyway? Remaining tickets will all be winners.`}
        confirmLabel="Begin drawing"
        confirmVariant="epic"
        loading={beginning}
        contentClassName="border-white/15 bg-zinc-950 text-white"
        onConfirm={runBeginDrawing}
      />
    </div>
  );
}

function WinnerStepper({
  value,
  entryCount,
  disabled,
  onChange,
}: {
  value: number;
  entryCount: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}) {
  const set = (next: number) =>
    onChange(clampRaffleWinnerCount(next, entryCount));

  return (
    <div
      data-name="RAFFLE_WINNER_COUNT"
      className="inline-flex items-center gap-2 self-start rounded-md border border-white/30 px-2 py-1"
    >
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className="text-white hover:bg-white/10"
        disabled={disabled || value <= 1}
        aria-label="Fewer winners"
        onClick={() => set(value - 1)}
      >
        <MinusIcon />
      </Button>
      <span className="min-w-16 text-center text-sm text-white">
        {value} {value === 1 ? 'winner' : 'winners'}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className="text-white hover:bg-white/10"
        disabled={disabled || value >= Math.max(1, entryCount)}
        aria-label="More winners"
        onClick={() => set(value + 1)}
      >
        <PlusIcon />
      </Button>
    </div>
  );
}

function useRaffleAdminSubmissionSync(
  engagementId: string,
  mockRealtime: boolean,
) {
  useOnSubmissionCreatedSubscription({
    variables: { engagementId },
    skip: !engagementId,
    onData: ({ data, client }) => {
      const submission = data.data?.submissionCreated?.submission;
      if (!submission) return;
      client.cache.updateQuery<GetSubmissionsQuery>(
        {
          query: GetSubmissionsDocument,
          variables: { engagementId },
        },
        (cachedData) => {
          if (!cachedData?.submissions) return cachedData;
          if (
            cachedData.submissions.some((item) => item.id === submission.id)
          ) {
            return cachedData;
          }
          return {
            submissions: [...cachedData.submissions, submission],
          };
        },
      );
    },
  });
  useOnMockSubmissionCreatedSubscription({
    variables: { engagementId },
    skip: !engagementId || !mockRealtime,
    onData: ({ data, client }) => {
      const submission = data.data?.mockSubmissionCreated?.submission;
      if (!submission) return;
      client.cache.updateQuery<GetSubmissionsQuery>(
        {
          query: GetSubmissionsDocument,
          variables: { engagementId },
        },
        (cachedData) => {
          if (!cachedData?.submissions) return cachedData;
          if (
            cachedData.submissions.some((item) => item.id === submission.id)
          ) {
            return cachedData;
          }
          return {
            submissions: [...cachedData.submissions, submission],
          };
        },
      );
    },
  });
  useOnSubmissionDeletedSubscription({
    variables: { engagementId },
    skip: !engagementId,
    onData: ({ data, client }) => {
      const submissionId = data.data?.submissionDeleted?.submissionId;
      if (submissionId) {
        client.cache.evict({ id: `Submission:${submissionId}` });
      }
    },
  });
  useOnMockSubmissionDeletedSubscription({
    variables: { engagementId },
    skip: !engagementId || !mockRealtime,
    onData: ({ data, client }) => {
      const submissionId = data.data?.mockSubmissionDeleted?.submissionId;
      if (submissionId) {
        client.cache.evict({ id: `Submission:${submissionId}` });
      }
    },
  });
}

export function AdminRaffleControlView({
  engagement,
}: {
  engagement: FullEngagementFragment;
}) {
  const typedEngagement = asTypedEngagement<
    RaffleAdminData,
    RaffleAdminConfig,
    RaffleViewData,
    RaffleViewConfig
  >(engagement);
  return <RaffleAdminControlViewInner engagement={typedEngagement} />;
}
